import { useState } from "react";
import { api, API, formatCPF, onlyDigits } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { FileText, Search, Download, Eye, Hash, IdCard, Loader2, FilePlus2, Paperclip } from "lucide-react";

export default function DossiePage() {
  const [modo, setModo] = useState("protocolo");
  const [protocolo, setProtocolo] = useState("");
  const [cpf, setCpf] = useState("");
  const [insc, setInsc] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = async (e) => {
    e?.preventDefault();
    setLoading(true); setInsc(null);
    try {
      let r;
      if (modo === "protocolo") {
        r = await api.get(`/inscricoes/protocolo/${protocolo.trim()}`);
      } else {
        r = await api.get("/inscricoes/consulta/cpf", { params: { cpf: onlyDigits(cpf) } });
      }
      // Precisamos do ID — buscar detalhes completos
      const protoAchado = r.data.protocolo;
      const listR = await api.get("/inscricoes", { params: { q: protoAchado, limit: 1 } });
      if (listR.data.items?.length) {
        const fullR = await api.get(`/inscricoes/${listR.data.items[0].id}`);
        setInsc(fullR.data);
      } else {
        toast.error("Inscrição não encontrada");
      }
    } catch (err) {
      logger.error("Falha ao buscar inscrição", err);
      toast.error(err.response?.data?.detail || "Falha ao buscar");
    } finally { setLoading(false); }
  };

  const abrirDossie = () => {
    if (!insc) return;
    window.open(`${API}/inscricoes/${insc.id}/dossie`, "_blank");
  };

  const docsCount = (insc?.documentos || []).length;

  return (
    <div className="space-y-6" data-testid="dossie-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-3">
          <FilePlus2 className="w-8 h-8" /> Dossiê de Inscritos
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Gera um PDF único consolidando a ficha completa de inscrição + todos os anexos (documentos digitalizados).
          Ideal para processos administrativos, auditoria TCU e análise de homologação.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="w-4 h-4" /> Localizar inscrição
          </CardTitle>
          <CardDescription>Busque por protocolo ou CPF do responsável familiar.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={modo} onValueChange={(v) => { setModo(v); setInsc(null); }}>
            <TabsList className="grid grid-cols-2 w-full max-w-md mb-3" data-testid="dossie-tabs">
              <TabsTrigger value="protocolo" data-testid="dossie-tab-protocolo">
                <Hash className="w-4 h-4 mr-2" /> Protocolo
              </TabsTrigger>
              <TabsTrigger value="cpf" data-testid="dossie-tab-cpf">
                <IdCard className="w-4 h-4 mr-2" /> CPF
              </TabsTrigger>
            </TabsList>
            <TabsContent value="protocolo">
              <form onSubmit={buscar} className="flex gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <Label className="sr-only">Protocolo</Label>
                  <Input
                    value={protocolo}
                    onChange={(e) => setProtocolo(e.target.value.toUpperCase())}
                    placeholder="GRAJAU-MCMV-2026-000001"
                    data-testid="dossie-input-protocolo"
                  />
                </div>
                <Button type="submit" disabled={loading || !protocolo.trim()}
                        className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="dossie-btn-protocolo">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Buscar
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="cpf">
              <form onSubmit={buscar} className="flex gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <Label className="sr-only">CPF</Label>
                  <Input
                    value={cpf}
                    onChange={(e) => setCpf(formatCPF(e.target.value))}
                    placeholder="000.000.000-00"
                    data-testid="dossie-input-cpf"
                  />
                </div>
                <Button type="submit" disabled={loading || onlyDigits(cpf).length !== 11}
                        className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="dossie-btn-cpf">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Buscar
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {insc && (
        <Card className="border-emerald-200" data-testid="dossie-preview">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Inscrição localizada
            </CardTitle>
            <CardDescription>Revise os dados antes de gerar o Dossiê.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm bg-slate-50 border rounded p-4">
              <div><strong>Protocolo:</strong> <span className="font-mono">{insc.protocolo}</span></div>
              <div><strong>Status:</strong> <Badge>{insc.status}</Badge></div>
              <div><strong>Nome:</strong> {insc.nome_completo}</div>
              <div><strong>CPF:</strong> {formatCPF(insc.cpf)}</div>
              <div><strong>Bairro:</strong> {insc.bairro}</div>
              <div><strong>Renda:</strong> R$ {Number(insc.renda_total || 0).toLocaleString("pt-BR")}</div>
              <div><strong>Dependentes:</strong> {insc.qtd_dependentes || 0}</div>
              <div><strong>Estado Civil:</strong> {insc.estado_civil}</div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 bg-blue-50 border border-blue-200 rounded p-3">
              <div className="text-sm flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-blue-600" />
                <span><strong>{docsCount}</strong> {docsCount === 1 ? "documento anexado" : "documentos anexados"} serão incluídos no PDF.</span>
              </div>
              {docsCount === 0 && (
                <span className="text-xs text-amber-700">
                  Dossiê conterá apenas a ficha (sem documentos digitalizados).
                </span>
              )}
            </div>

            <div className="flex gap-2 flex-wrap justify-end">
              <Button
                variant="outline"
                onClick={() => window.open(`${API}/inscricoes/${insc.id}/comprovante`, "_blank")}
                data-testid="dossie-btn-comprovante"
              >
                <Eye className="w-4 h-4 mr-2" /> Ver só a Ficha
              </Button>
              <Button
                onClick={abrirDossie}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="dossie-btn-gerar"
              >
                <Download className="w-4 h-4 mr-2" /> Gerar Dossiê Completo (PDF)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
